import React, { useContext, useEffect, useState } from 'react';
import { UserRoleContext } from '../context/UserRoleContext';
import { EventContext } from '../context/EventContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { getRolesByUserId } from '../services/userRoleService';
import logo from '../assets/logo.png';
import { UserRole } from '../types/UserRole';
import { useNavigate } from 'react-router-dom';
import { getLogoDownloadUrl } from '../services/logoService';
import { ROUTES } from '../constants/routes';
import { fetchAuthSession } from '@aws-amplify/auth';
import { getEvent } from '../services/eventService';


interface NavbarProps {
  setSidebarOpen: (open: boolean) => void;
  setSelectedTab: (tab: string) => void;
  isSidebarOpen: boolean
}

const Navbar: React.FC<NavbarProps> = ({ setSidebarOpen, setSelectedTab, isSidebarOpen }) => {
  const { userRole, setUserRole } = useContext(UserRoleContext);
  const { currentEvent, setCurrentEvent } = useContext(EventContext);
  const { signOut, isAuthenticated, user } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [roleHeader, setRoleHeader] = useState<string>('Select Role');

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  const [logoUrl, setLogoUrl] = useState('');


  const formatRoleHeader = (role: UserRole | null) => {
    if (role) {
      if (role.host_id) {
        return `${role.host_name} - ${role.role_name}`;
      } else {
        return `${role.organization_name} - ${role.role_name}`;
      }
    }
    return 'Select Role';
  };

  const handleRoleSelect = (role: UserRole) => {
    setUserRole(role);
  };

  useEffect(() => {
    setDropdownOpen(false);
    setRoleHeader(formatRoleHeader(userRole));
  }, [userRole]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'logout') {
        signOut();
        navigate(ROUTES.LOGIN);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [signOut]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'login') {
        fetchAuthSession();
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

    useEffect(() => {
    const fetchEvent = async () => {
      const stored = localStorage.getItem('currentEvent');
      if (!stored) return;

      try {
        const parsed = JSON.parse(stored);
        const event = await getEvent(parsed.event_id);
        //console.log(parsed);
        setCurrentEvent(event);
      } catch (error) {
        console.error('Failed to fetch event:', error);
      }
    };
    if (isAuthenticated) {
    const screenWidth = window.innerWidth;
    if (screenWidth > 700) {
      setSidebarOpen(true);
    }
    fetchEvent();

    }
  }, [isAuthenticated]);

  const fetchRoles = async () => {
    try {
      if (user?.userId) {
        const roles = await getRolesByUserId(user.userId);
        if (roles.length > 0) {
          setUserRoles(roles);
          const matchedRole = roles.find(role => role.user_role_id === userRole?.user_role_id);

          if (matchedRole) {
            // If a match is found, update the role and the header
            setUserRole(matchedRole);
            setRoleHeader(formatRoleHeader(matchedRole));
          } else if (roles.length == 1) {
            setUserRole(roles[0]);
            setRoleHeader(formatRoleHeader(roles[0]));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch user roles:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchRoles();
    } else {
      setUserRoles([]);
      setRoleHeader('Select Role');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchLogoUrl = async () => {
      if (currentEvent && currentEvent.logo != '') {
        try {
          if (currentEvent.logo) {
            const result = await getLogoDownloadUrl(currentEvent.event_id, currentEvent.logo);
            if (result) {
              setLogoUrl(result);
            }
          }
        } catch (error) {
          console.error('Error fetching logo download URL:', error);
        }
      }
    };
    fetchLogoUrl();
  }, [currentEvent?.event_id, currentEvent?.logo]);

  const goToMainDashboard = () => {
    navigate('/');
  };

  const logoutClicked = () => {
    setSidebarOpen(false);
    signOut();
    addToast('Successfully Logged Out!');
    navigate(ROUTES.LOGIN);
  };

  const styles = {
    navbar: {
      position: 'fixed' as 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      padding: '0',//'0.5rem 2rem',
      backgroundColor: '#FFF',//'#E6E6E6',//#333
      color: '#000',//#fff
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'

    },
    logoContainer: {
      cursor: 'pointer',
      flex: 1,
    },
    logo: {
      maxWidth: '20vw',
      maxHeight: '52px',
    },
    eventLogo: {
      maxWidth: '40px',
      maxHeight: '40px',
      borderRadius: '5px',
      marginLeft: '5px',
      marginRight: '5px'
    },
    eventSection: {
      fontSize: 'clamp(12px, 18px, 20px)', 
      fontStyle: 'italic',
      flex: 2,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
      textAlign: 'center' as const,
    } as React.CSSProperties,
    roleSection: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginLeft: '1rem',
      marginRight: '10px',
    },
    roleButton: {
      backgroundColor: '#555',
      color: '#fff',
      padding: '0.5rem 1rem',
      borderRadius: '5px',
      cursor: 'pointer',
      border: 'none',
      marginLeft: '1rem',
      maxWidth: '25vw',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: 'clamp(10px, 18px, 20px)', 
    },
    dropdownMenu: {
      position: 'absolute' as 'absolute',
      top: '100%',
      right: '10px',
      backgroundColor: '#444',
      color: '#fff',
      borderRadius: '5px',
      overflow: 'auto',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
      display: dropdownOpen ? 'block' : 'none',
    },
    dropdownItem: {
      padding: '0.5rem 1rem',
      cursor: 'pointer',
    },
    hamburgerMenu: {
      display: 'block',
      cursor: 'pointer',
      fontSize: '1.5rem',
      padding: '10px',
      backgroundColor: 'transparent',
      border: 'none',
      color: '#000',//#fff
      outline: 'none',
      boxShadow: 'none'
    },
  };

  return (
    <nav style={styles.navbar}>
      {isAuthenticated && <button onClick={() => {setSidebarOpen(!isSidebarOpen);}} style={styles.hamburgerMenu}>
        &#9776;
      </button>}

      {!isAuthenticated && (
        <button style={{ ...styles.hamburgerMenu, color: 'transparent', cursor: 'default' }}>
          &#9776;
        </button>
      )}

      <div style={styles.logoContainer} onClick={goToMainDashboard}>
        <img src={logo} alt="Cyne Up" style={styles.logo} />
      </div>

      {currentEvent && (
        <div style={styles.eventSection}>
          {logoUrl && (
            <img src={logoUrl} style={styles.eventLogo} />
          )}
          {currentEvent.event_name}{currentEvent.is_locked ? '\n***LOCKED***' : currentEvent?.event_open ? '' : '\n***CLOSED***'}

        </div>
      )}

      {isAuthenticated && (
        <div style={styles.roleSection}>
          <button onClick={toggleDropdown} style={styles.roleButton}>
            {roleHeader}
          </button>

          <div style={styles.dropdownMenu}>
            {dropdownOpen &&
              userRoles.map((role) => (
                <div
                  key={role.user_role_id}
                  style={styles.dropdownItem}
                  onClick={() => handleRoleSelect(role)}
                >
                  {(role.organization_id
                    ? `${role.organization_name} - ${role.role_name}`
                    : `${role.host_name} - ${role.role_name}`)}
                  {role.user_role_id === userRole?.user_role_id && (
                    <span style={{ marginLeft: '10px', color: 'green' }}>
                      &#10003;
                    </span>
                  )}
                </div>
              ))}
            <div
              style={styles.dropdownItem}
              onClick={() => {
                navigate(ROUTES.USER_PROFILE);
                toggleDropdown();
              }}
            >
              Profile
            </div>
            {/* <div
              style={styles.dropdownItem}
              onClick={() => {
                navigate(ROUTES.HOST_ORG_OVERVIEW);
                toggleDropdown();
              }}
            >
              Create New Host
            </div> */}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
